import { useDirection } from "@radix-ui/react-direction";
import { ChevronDown, ChevronUp } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { PATHS } from "@/constants/paths";
import { useMediaDownloadManager } from "@/hooks/use-media-download";
import { cn } from "@/lib/utils";

import { DownloadIndicatorItem } from "./DownloadIndicatorItem";

// NOTE: App-level, always-mounted indicator (rendered by MediaDownloadProvider).
// A single collapsible container that lists every in-flight media download — with
// live progress, a cancel control, and a click-through to the meeting — so the
// user always knows whether a download is running, done, or failed (NSAI-3838).
// Anchored bottom-right (bottom-left in RTL) and lifted above the footer — plus
// the play bar on a meeting page — so it never covers the play-bar download
// button. The download for the meeting currently open is hidden here (the play
// bar shows it inline), so this only carries the other / background downloads.
const DownloadIndicatorComponent = (): JSX.Element | null => {
  const { t } = useTranslation("callAssistant");
  const dir = useDirection();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeDownloads, cancelDownload } = useMediaDownloadManager();
  const [isExpanded, setIsExpanded] = useState(true);

  const currentMeetingUuid = matchPath(PATHS.MEETING_DETAILS, location.pathname)
    ?.params.meetingUuid;
  const isOnMeetingPage = currentMeetingUuid !== undefined;

  const visibleDownloads = useMemo(
    () =>
      activeDownloads.filter(
        (download) => download.meetingUuid !== currentMeetingUuid,
      ),
    [activeDownloads, currentMeetingUuid],
  );

  const overallProgress = useMemo(() => {
    if (visibleDownloads.length === 0) {
      return 0;
    }
    const total = visibleDownloads.reduce(
      (sum, download) => sum + download.downloadProgress,
      0,
    );
    return Math.round(total / visibleDownloads.length);
  }, [visibleDownloads]);

  const handleOpen = useCallback(
    (meetingUuid: string): void => {
      navigate(generatePath(PATHS.MEETING_DETAILS, { meetingUuid }));
    },
    [navigate],
  );

  const handleToggle = useCallback((): void => {
    setIsExpanded((previous) => !previous);
  }, []);

  if (visibleDownloads.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed z-50 flex max-h-[calc(100vh-2rem)] w-80 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border bg-background shadow-lg",
        dir === "rtl" ? "left-4" : "right-4",
        // NOTE: Clear the app footer everywhere, and the play bar too on a meeting
        // page, so the container never overlaps the play-bar download button.
        isOnMeetingPage
          ? "bottom-[calc(var(--recording-play-bar-height)_+_var(--footer-height)_+_1rem)]"
          : "bottom-[calc(var(--footer-height)_+_1rem)]",
      )}
      data-testid="download-indicator"
    >
      <button
        type="button"
        onClick={handleToggle}
        className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2 text-sm font-medium hover:bg-accent"
        aria-expanded={isExpanded}
        data-testid="download-indicator-toggle"
      >
        <span>
          {t("details.downloads.title", { total: visibleDownloads.length })}
          {!isExpanded && ` · ${overallProgress}%`}
        </span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0" />
        )}
      </button>
      {isExpanded && (
        <div
          className="flex flex-col gap-1 overflow-y-auto p-1"
          data-testid="download-indicator-list"
        >
          {visibleDownloads.map((download) => (
            <DownloadIndicatorItem
              key={download.meetingUuid}
              meetingUuid={download.meetingUuid}
              downloadingType={download.downloadingType}
              downloadProgress={download.downloadProgress}
              label={download.label}
              onOpen={handleOpen}
              onCancel={cancelDownload}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const DownloadIndicator = memo(DownloadIndicatorComponent);
